import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, CalendarDays, MapPin, Pencil, Trash2, Users, FileText, Zap } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/apiClient';
import { Button, Badge, Modal, Input, Textarea, SelectField, PageHeader, EmptyState, ConfirmDialog, Spinner, cn } from '../components/ui';
import type { Session, Profile } from '@conference/shared';
import { ProgramImportPanel } from '../components/ProgramImportPanel';
import DraftRecordDrawer from '../components/ingestion/DraftRecordDrawer';
import ReviewTable from '../components/ingestion/ReviewTable';
import { contentIngestionService, type DraftChange } from '../services/contentIngestionService';

// ─── Schema ───────────────────────────────────────────────────────────────────

const sessionSchema = z.object({
  title:       z.string().min(3),
  description: z.string().optional(),
  type:        z.enum(['keynote', 'talk', 'workshop', 'panel', 'poster', 'break', 'networking', 'social']),
  track:       z.string().optional(),
  date:        z.string().min(1, 'Date is required'),
  start_time:  z.string().min(1, 'Start time required'),
  end_time:    z.string().min(1, 'End time required'),
  room:        z.string().optional(),
  status:      z.enum(['scheduled', 'live', 'ended', 'cancelled']),
  is_featured: z.boolean().default(false),
  speaker_ids: z.array(z.string()).default([]),
  paper_ids:   z.array(z.string()).default([]),
});

type SessionForm = z.input<typeof sessionSchema>;

export function formatSessionDate(date: string | null | undefined) {
  if (!date) return 'Date to be scheduled';

  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return 'Date to be scheduled';

  return parsed.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

export function formatSessionTime(time: string | null | undefined) {
  return time?.slice(0, 5) || 'TBD';
}

function typeBadge(type: Session['type'] | string | null | undefined) {
  const map: Record<string, { variant: 'info' | 'purple' | 'warning' | 'success' | 'default', label: string }> = {
    keynote:  { variant: 'info',    label: 'Keynote'  },
    talk:     { variant: 'default', label: 'Talk'     },
    workshop: { variant: 'purple',  label: 'Workshop' },
    panel:    { variant: 'warning', label: 'Panel'    },
    poster:   { variant: 'default', label: 'Poster'   },
    break:    { variant: 'default', label: 'Break'    },
    networking: { variant: 'success', label: 'Networking' },
    social:   { variant: 'success', label: 'Social'   },
  };
  const { variant, label } = map[type ?? ''] ?? {
    variant: 'default',
    label: type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Session',
  };
  return <Badge variant={variant}>{label}</Badge>;
}

// ─── Session form ─────────────────────────────────────────────────────────────

type PaperBrief = {
  id: string;
  title: string;
  authors?: string[];
  processing_status?: string;
  presentation_topic?: string | null;
  presentation_start_time?: string | null;
  presentation_end_time?: string | null;
};

type SessionWithSpeakers = Omit<Session, 'speakers'> & {
  speakers?: Profile[];
  papers?: PaperBrief[];
};

function SessionModal({ open, onClose, session, eventId, speakers, draftSpeakers, papers, onSaved, ingestionEnabled = false }: {
  open: boolean; onClose: () => void;
  session: SessionWithSpeakers | null; eventId: string;
  speakers: Profile[]; draftSpeakers: DraftChange[]; papers: PaperBrief[]; onSaved: () => void; ingestionEnabled?: boolean;
}) {
  const { register, handleSubmit, reset, control, formState: { errors, isSubmitting } } = useForm<SessionForm>({
    resolver: zodResolver(sessionSchema),
    defaultValues: { title: '', type: 'talk', date: '', start_time: '', end_time: '', status: 'scheduled', speaker_ids: [], paper_ids: [], is_featured: false },
  });
  const [pendingSave, setPendingSave] = useState<SessionForm | null>(null);

  useEffect(() => {
    if (!open) return;
    if (session) {
      const speakerIds = session.speakers?.map((speaker) => speaker.id) ?? [];
      const paperIds = session.papers?.map((p) => String(p.id)) ?? [];
      const type = ['keynote', 'talk', 'workshop', 'panel', 'poster', 'break', 'networking', 'social'].includes(session.type)
        ? session.type
        : 'talk';
      reset({
        ...session,
        title: session.title ?? '',
        type,
        date: session.date ?? '',
        start_time: session.start_time ?? '',
        end_time: session.end_time ?? '',
        status: session.status ?? 'scheduled',
        is_featured: session.is_featured ?? false,
        speaker_ids: speakerIds,
        paper_ids: paperIds,
        description: session.description ?? '',
        track: session.track ?? '',
        room: session.room ?? '',
      });
    } else {
      reset({ title: '', type: 'talk', date: '', start_time: '', end_time: '', status: 'scheduled', speaker_ids: [], paper_ids: [], is_featured: false });
    }
  }, [open, session, reset]);

  const persist = async (data: SessionForm) => {
    const { speaker_ids, paper_ids, ...sessionData } = data;
    const payload = {
      ...sessionData,
      event_id: eventId,
      description: sessionData.description || null,
      track: sessionData.track || null,
      room: sessionData.room || null,
      is_featured: sessionData.is_featured ?? false,
      speaker_ids: speaker_ids ?? [],
      paper_ids: paper_ids ?? [],
    };

    try {
      if (ingestionEnabled) {
        const { event_id: _eventId, speaker_ids: selectedSpeakers, paper_ids: _paperIds, ...draftData } = payload;
        const speakerChangeIds = selectedSpeakers.filter((id) => id.startsWith('draft:')).map((id) => id.slice(6));
        const speakerProfileIds = selectedSpeakers.filter((id) => !id.startsWith('draft:'));
        const content = { ...draftData, speaker_profile_ids: speakerProfileIds };
        const change = session
          ? await contentIngestionService.stagePublishedUpdate(eventId, 'session', session.id, content)
          : await contentIngestionService.createDraft(eventId, 'session', content);
        await Promise.all(speakerChangeIds.map((speakerChangeId) =>
          contentIngestionService.createRelationship(eventId, change.id, { to_change_id: speakerChangeId })));
      } else if (session) await apiClient.patch(`/sessions/${session.id}`, payload);
      else await apiClient.post('/sessions', payload);
      toast.success(ingestionEnabled ? 'Session draft saved for review' : session ? 'Session updated' : 'Session created');
      setPendingSave(null); onSaved(); onClose();
    } catch (err) {
      toast.error(session ? 'Update failed' : 'Create failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    }
  };
  const prepareSave = (data: SessionForm) => session ? setPendingSave(data) : persist(data);

  return (
    <Modal open={open} onClose={onClose} title={session ? 'Edit Session' : 'Add Session'} size="xl">
      <form onSubmit={handleSubmit(prepareSave)} className="space-y-4">
        <Input label="Title *" placeholder="Opening Keynote: The Next Frontier…" error={errors.title?.message} {...register('title')} />
        <Textarea label="Description" placeholder="Session abstract or description…" rows={2} {...register('description')} />

        <div className="grid grid-cols-3 gap-3">
          <SelectField
            label="Type *"
            options={['keynote','talk','workshop','panel','poster','break','networking','social'].map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
            {...register('type')}
          />
          <Input label="Track" placeholder="Engineering, Research…" {...register('track')} />
          <SelectField
            label="Status"
            options={['scheduled','live','ended','cancelled'].map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            {...register('status')}
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Input label="Date *" type="date" error={errors.date?.message} {...register('date')} />
          <Input label="Start Time *" type="time" error={errors.start_time?.message} {...register('start_time')} />
          <Input label="End Time *"   type="time" error={errors.end_time?.message}   {...register('end_time')} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Room / Location" placeholder="Grand Hall A" {...register('room')} />
          <div className="flex items-end pb-2.5">
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" className="accent-sky-500" {...register('is_featured')} />
              Featured session
            </label>
          </div>
        </div>

        {/* Speaker multi-select */}
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-gray-300">Speakers</p>
          <Controller
            control={control}
            name="speaker_ids"
            render={({ field }) => {
              const value = field.value ?? [];
              return (
              <div className="flex flex-wrap gap-2">
                {speakers.map((sp) => {
                  const selected = value.includes(sp.id);
                  return (
                    <button
                      key={sp.id}
                      type="button"
                      onClick={() => {
                        field.onChange(
                          selected ? value.filter((id) => id !== sp.id) : [...value, sp.id]
                        );
                      }}
                      className={cn(
                        'px-3 py-1.5 rounded-lg text-xs border transition-colors',
                        selected
                          ? 'bg-sky-500/20 border-sky-500/50 text-sky-300'
                          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600',
                      )}
                    >
                      {sp.name}
                    </button>
                  );
                })}
                {ingestionEnabled && draftSpeakers.map((change) => {
                  const id = `draft:${change.id}`;
                  const name = String(change.approved_data.display_name ?? 'Untitled speaker');
                  const selected = value.includes(id);
                  return <button key={id} type="button" onClick={() => field.onChange(
                    selected ? value.filter((item) => item !== id) : [...value, id]
                  )} className={cn('px-3 py-1.5 rounded-lg text-xs border transition-colors', selected
                    ? 'bg-sky-500/20 border-sky-500/50 text-sky-300'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600')}>{name} (draft)</button>;
                })}
                {speakers.length === 0 && (!ingestionEnabled || draftSpeakers.length === 0) && (
                  <p className="text-xs text-gray-500">No speakers found — add speakers first.</p>
                )}
              </div>
              );
            }}
          />
        </div>

        {/* Paper multi-select */}
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-gray-300">Linked Papers</p>
          {ingestionEnabled ? <p className="text-xs text-amber-300">Paper links are not changed by Milestone 1 drafts. Publish the schedule first, then manage paper links through the existing paper workflow.</p> : <>
          <p className="text-xs text-gray-500">Select papers that will be presented in this session.</p>
          <Controller
            control={control}
            name="paper_ids"
            render={({ field }) => {
              const value = field.value ?? [];
              return (
              <div className="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1">
                {papers.map((paper) => {
                  const pid = String(paper.id);
                  const selected = value.includes(pid);
                  return (
                    <button
                      key={pid}
                      type="button"
                      onClick={() => {
                        field.onChange(
                          selected ? value.filter((id) => id !== pid) : [...value, pid]
                        );
                      }}
                      className={cn(
                        'flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-left border transition-colors',
                        selected
                          ? 'bg-violet-500/20 border-violet-500/50 text-violet-300'
                          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600',
                      )}
                    >
                      <FileText size={13} className={cn('mt-0.5 shrink-0', selected ? 'text-violet-400' : 'text-gray-500')} />
                      <div className="min-w-0">
                        <p className={cn('text-xs font-medium truncate', selected ? 'text-violet-200' : 'text-gray-300')}>
                          {paper.title}
                        </p>
                        {paper.authors && paper.authors.length > 0 && (
                          <p className="text-[10px] text-gray-500 truncate mt-0.5">
                            {paper.authors.slice(0, 3).join(', ')}{paper.authors.length > 3 ? ' et al.' : ''}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
                {papers.length === 0 && (
                  <p className="text-xs text-gray-500">No papers uploaded for this event yet.</p>
                )}
              </div>
              );
            }}
          />
          </>}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={isSubmitting}>{session ? 'Save Changes' : 'Add Session'}</Button>
        </div>
      </form>
      <ConfirmDialog open={pendingSave !== null} onClose={() => setPendingSave(null)} onConfirm={() => pendingSave && persist(pendingSave)} intent="primary" title="Save changes?" description="Apply the current session changes?" confirmLabel="Save changes" />
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export type SessionDisplayItem = {
  sourceSession: SessionWithSpeakers;
  title: string;
  start_time: string | null | undefined;
  end_time: string | null | undefined;
  sourceSessionTitle?: string;
  papers?: PaperBrief[];
};

export function buildSessionDisplayItems(sessions: SessionWithSpeakers[]): SessionDisplayItem[] {
  return sessions.flatMap((session) => {
    const papers = session.papers ?? [];
    if (session.type !== 'talk' || !papers.some((paper) => paper.presentation_topic)) {
      return [{ sourceSession: session, title: session.title, start_time: session.start_time, end_time: session.end_time, papers }];
    }
    const byTopic = new Map<string, PaperBrief[]>();
    papers.forEach((paper) => {
      if (!paper.presentation_topic) return;
      byTopic.set(paper.presentation_topic, [...(byTopic.get(paper.presentation_topic) ?? []), paper]);
    });
    return [...byTopic].map(([topic, topicPapers]) => {
      const orderedPapers = [...topicPapers].sort((left, right) => (
        (left.presentation_start_time ?? '').localeCompare(right.presentation_start_time ?? '')
        || left.title.localeCompare(right.title)
      ));
      return {
        sourceSession: session,
        title: topic,
        start_time: orderedPapers[0]?.presentation_start_time ?? session.start_time,
        end_time: orderedPapers.at(-1)?.presentation_end_time ?? session.end_time,
        sourceSessionTitle: session.title,
        papers: orderedPapers,
      };
    });
  }).sort((left, right) => (
    (left.sourceSession.date ?? '').localeCompare(right.sourceSession.date ?? '')
    || (left.start_time ?? '').localeCompare(right.start_time ?? '')
    || left.title.localeCompare(right.title)
  ));
}

export default function SessionsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [sessions, setSessions]   = useState<SessionWithSpeakers[]>([]);
  const [speakers, setSpeakers]   = useState<Profile[]>([]);
  const [papers, setPapers]       = useState<PaperBrief[]>([]);
  const [draftSpeakers, setDraftSpeakers] = useState<DraftChange[]>([]);
  const [eventName, setEventName] = useState('');
  const [loading, setLoading]     = useState(true);
  const [modalOpen, setModal]     = useState(false);
  const [editing, setEditing]     = useState<SessionWithSpeakers | null>(null);
  const [deleteTarget, setDel]    = useState<SessionWithSpeakers | null>(null);
  const [deleting, setDeleting]   = useState(false);
  const [prewarming, setPrewarming] = useState(false);
  const [ingestionEnabled, setIngestionEnabled] = useState(false);
  const [view, setView] = useState<'published' | 'draft'>('published');
  const [drafts, setDrafts] = useState<DraftChange[]>([]);
  const [editingDraft, setEditingDraft] = useState<DraftChange | null>(null);

  async function load() {
    if (!eventId) return;
    setLoading(true);
    try {
      const [eventData, sessionsData, speakersData, papersData] = await Promise.all([
        apiClient.get<{ id: string; name: string; effective_content_ingestion_enabled?: boolean }>(`/admin/events/${eventId}`),
        apiClient.get<SessionWithSpeakers[]>(`/sessions?event_id=${eventId}`),
        apiClient.get<Profile[]>(`/speakers/all?event_id=${eventId}`),
        apiClient.get<PaperBrief[]>(`/papers?event_id=${eventId}`),
      ]);
      setEventName(eventData.name);
      const enabled = Boolean(eventData.effective_content_ingestion_enabled);
      setIngestionEnabled(enabled);
      setSessions(sessionsData);
      setSpeakers(speakersData);
      setPapers(papersData);
      setDraftSpeakers(enabled ? (await contentIngestionService.drafts(eventId, { area: 'speakers' }))
        .filter((change) => change.operation === 'create' && ['review_required', 'ready'].includes(change.status)) : []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [eventId]);

  async function loadDrafts() {
    if (!eventId) return;
    setDrafts(await contentIngestionService.drafts(eventId, { area: 'schedule' }));
  }

  async function refresh() {
    if (view === 'draft') await loadDrafts();
    else await load();
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (ingestionEnabled) await contentIngestionService.stagePublishedDelete(eventId ?? '', 'session', deleteTarget.id);
      else await apiClient.delete(`/sessions/${deleteTarget.id}`);
      toast.success(ingestionEnabled ? 'Delete draft created for review' : 'Session deleted');
      setDel(null);
      await refresh();
    } catch (err) {
      toast.error('Delete failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setDeleting(false);
    }
  }

  async function handlePrewarm() {
    if (!eventId || prewarming) return;
    setPrewarming(true);
    try {
      const result = await apiClient.post<{ queued_sessions: number; queued_papers: number }>(
        `/admin/events/${eventId}/prewarm-ai`, {}
      );
      if (result.queued_sessions === 0 && result.queued_papers === 0) {
        toast.success('All AI briefs already generated', { description: 'Nothing new to queue.' });
      } else {
        toast.success('AI pre-warm queued', {
          description: `${result.queued_sessions} session brief${result.queued_sessions !== 1 ? 's' : ''} and ${result.queued_papers} paper insight${result.queued_papers !== 1 ? 's' : ''} generating in background.`,
        });
      }
    } catch (err) {
      toast.error('Pre-warm failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setPrewarming(false);
    }
  }

  // Group the same topic-level entries attendees see by date.
  const byDate = buildSessionDisplayItems(sessions).reduce<Record<string, SessionDisplayItem[]>>((acc, item) => {
    (acc[item.sourceSession.date || '__unscheduled__'] ??= []).push(item);
    return acc;
  }, {});

  return (
    <div className="p-8">
      <PageHeader
        title="Sessions"
        description={`Manage sessions for ${eventName || '…'}`}
        breadcrumb={<Link to="/events" className="hover:text-gray-300">Events</Link>}
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              icon={<Zap size={14} />}
              onClick={handlePrewarm}
              loading={prewarming}
              title="Pre-generate AI prep briefs and paper insights for all sessions/papers that don't have them yet"
            >
              Pre-warm AI
            </Button>
            <Button icon={<Plus size={14} />} onClick={() => { setEditing(null); setModal(true); }}>Add Session</Button>
          </div>
        }
      />

      {ingestionEnabled && <div className="mb-5 flex gap-2" role="group" aria-label="Session content view"><Button size="sm" variant={view === 'published' ? 'primary' : 'secondary'} onClick={() => setView('published')}>Published</Button><Button size="sm" variant={view === 'draft' ? 'primary' : 'secondary'} onClick={() => { setView('draft'); void loadDrafts(); }}>Draft</Button></div>}

      {view === 'published' && eventId && <ProgramImportPanel eventId={eventId} onCommitted={load} />}

      {view === 'draft' ? <ReviewTable eventId={eventId ?? ''} changes={drafts} onEdit={setEditingDraft} onChanged={loadDrafts} /> : loading ? (
        <div className="flex justify-center py-20"><Spinner className="w-6 h-6 text-gray-500" /></div>
      ) : sessions.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="No sessions yet"
          description="Add the first session to this event."
          action={<Button icon={<Plus size={14} />} onClick={() => setModal(true)}>Add Session</Button>}
        />
      ) : (
        <div className="space-y-6">
          {Object.entries(byDate).sort().map(([date, daySessions]) => (
            <div key={date}>
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {formatSessionDate(date === '__unscheduled__' ? null : date)}
              </h2>
              <div className="space-y-2">
                {daySessions.map((item) => {
                  const session = item.sourceSession;
                  const sessionSpeakers = session.speakers ?? [];
                  return (
                    <div key={`${session.id}:${item.title}`} className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-start gap-4 hover:border-gray-700 transition-colors">
                      {/* Time */}
                      <div className="w-20 flex-shrink-0 text-right">
                        <p className="text-xs font-medium text-white">{formatSessionTime(item.start_time)}</p>
                        <p className="text-xs text-gray-500">{formatSessionTime(item.end_time)}</p>
                      </div>
                      {/* Divider */}
                      <div className="w-px self-stretch bg-gray-700 flex-shrink-0" />
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {typeBadge(session.type)}
                          {session.track && <span className="text-xs text-gray-500">{session.track}</span>}
                          {session.is_featured && <Badge variant="warning">Featured</Badge>}
                        </div>
                        <p className="text-sm font-medium text-white">{item.title}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400 flex-wrap">
                          {item.sourceSessionTitle && <span>Presentations · {item.sourceSessionTitle}</span>}
                          {session.room && <span className="flex items-center gap-1"><MapPin size={10} />{session.room}</span>}
                          {sessionSpeakers.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Users size={10} />
                              {sessionSpeakers.map((sp) => sp.name).join(', ')}
                            </span>
                          )}
                          {item.papers && item.papers.length > 0 && (
                            <span className="flex items-center gap-1 text-violet-400">
                              <FileText size={10} />
                              {item.papers.length} paper{item.papers.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button aria-label={`Edit ${session.title}`} variant="ghost" size="sm" icon={<Pencil size={12} />} onClick={() => { setEditing(session); setModal(true); }} />
                        <Button aria-label={`Delete ${session.title}`} variant="ghost" size="sm" icon={<Trash2 size={12} />} className="hover:text-red-400" onClick={() => setDel(session)} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <SessionModal
        open={modalOpen}
        onClose={() => setModal(false)}
        session={editing}
        eventId={eventId ?? ''}
        speakers={speakers}
        draftSpeakers={draftSpeakers}
        papers={papers}
        ingestionEnabled={ingestionEnabled}
        onSaved={refresh}
      />
      <DraftRecordDrawer eventId={eventId ?? ''} change={editingDraft} onClose={() => setEditingDraft(null)} onSaved={() => { setEditingDraft(null); void loadDrafts(); }} />
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDel(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={ingestionEnabled ? 'Stage session deletion?' : 'Delete Session'}
        description={ingestionEnabled ? `Create a reviewed delete draft for "${deleteTarget?.title}"? The live session will remain until publication.` : `Delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel={ingestionEnabled ? 'Create delete draft' : 'Delete'}
      />
    </div>
  );
}
